/**
 * Guided tours (the "coach marks" shown to a user once).
 *
 * WHY a slice and not a local hook: a tour is started from one component (the
 * user opens the Strategies view) but rendered by another (the overlay lives at
 * app level so it can escape every `overflow: hidden` container), and the
 * "already seen" list comes from the server via the `me` slice.
 *
 * Persistence is SERVER-SIDE (`me.toursSeen`, `POST /api/me/tours/{id}`), so a
 * tour shown once stays shown-once on a new browser or a second device. The
 * older hints this replaces kept nothing at all and so came back forever.
 *
 * `justSeen` is the optimistic half: the POST is fire-and-forget, and we must
 * not re-open the tour in the second between finishing it and `/api/me`
 * refreshing. Effective seen = server list ∪ justSeen — see `selectTourSeen`.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";

/** Tour ids. Must match TOUR_IDS in backend/app/auth/store.py. */
export const TOUR_MAIN = "main";
export const TOUR_STRATEGIES = "strategies";
export const TOUR_SIGNAL_ORDERS = "signal_orders";

export type TourId =
  | typeof TOUR_MAIN
  | typeof TOUR_STRATEGIES
  | typeof TOUR_SIGNAL_ORDERS;

interface TourState {
  /** The tour currently on screen, or null when nothing is running. */
  active: TourId | null;
  /** Index into that tour's *visible* steps. */
  step: number;
  /** Tours finished/skipped in this session, before /api/me catches up. */
  justSeen: TourId[];
  /** Tours we have already decided not to auto-start again this session (e.g.
   *  the anchors were missing). Prevents a start/stop flicker loop without
   *  burning the server-side "seen" flag. */
  skippedThisSession: TourId[];
}

const initialState: TourState = {
  active: null,
  step: 0,
  justSeen: [],
  skippedThisSession: [],
};

/** Tell the server this user has seen a tour. Deliberately swallows errors: a
 *  failed mark means the tour may play once more, which is a far better failure
 *  than an error surfacing over the UI during onboarding. */
export const markTourSeen = createAsyncThunk<void, TourId>(
  "tour/markSeen",
  async (tourId) => {
    try {
      await api.markTourSeen(tourId);
    } catch {
      // Ignored on purpose — see above.
    }
  },
);

/** Replay every tour for this user ("show me that again"). */
export const replayTours = createAsyncThunk<void, void>(
  "tour/replay",
  async () => {
    await api.resetTours();
  },
);

const tourSlice = createSlice({
  name: "tour",
  initialState,
  reducers: {
    startTour: (state, action: PayloadAction<TourId>) => {
      if (state.active === action.payload) return;
      state.active = action.payload;
      state.step = 0;
    },
    nextStep: (state) => {
      state.step += 1;
    },
    prevStep: (state) => {
      state.step = Math.max(0, state.step - 1);
    },
    /** Jump to a step — used when a step's anchor turns out to be missing. */
    goToStep: (state, action: PayloadAction<number>) => {
      state.step = Math.max(0, action.payload);
    },
    /** Finished or dismissed: never auto-start this tour again. */
    endTour: (state, action: PayloadAction<TourId>) => {
      if (!state.justSeen.includes(action.payload)) {
        state.justSeen.push(action.payload);
      }
      if (state.active === action.payload) {
        state.active = null;
        state.step = 0;
      }
    },
    /** Stop trying to start this tour for now, WITHOUT marking it seen. */
    deferTour: (state, action: PayloadAction<TourId>) => {
      if (!state.skippedThisSession.includes(action.payload)) {
        state.skippedThisSession.push(action.payload);
      }
      if (state.active === action.payload) {
        state.active = null;
        state.step = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(replayTours.fulfilled, (state) => {
      state.justSeen = [];
      state.skippedThisSession = [];
      state.active = null;
      state.step = 0;
    });
  },
});

export const {
  startTour,
  nextStep,
  prevStep,
  goToStep,
  endTour,
  deferTour,
} = tourSlice.actions;

export default tourSlice.reducer;
